'use client';

import React, { useState, useEffect } from 'react';
import FilterPanel, { type FilterState } from './FilterPanel';
import PetCard, { type Pet } from './PetCard';
import ComparisonPanel from './ComparisonPanel';
import SavedSearches from './SavedSearches';
import Icon from '@/components/ui/AppIcon';
import { fetchPets, formatAge, getCategoryDisplay, mapCategoryToEnum } from '@/utils/petService';
import { savedSearchService, SavedSearch } from '@/services/savedSearchService';
import { useAuth } from '@/contexts/AuthContext';
import type { Pet as DatabasePet } from '@/utils/petService';
import { StaggerContainer, StaggerItem } from '@/components/ui/motion';

const ITEMS_PER_PAGE = 12;

const PetDiscoveryInteractive = () => {
  const { user } = useAuth();
  const [isHydrated, setIsHydrated] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [sortBy, setSortBy] = useState('compatibility');
  const [compareList, setCompareList] = useState<string[]>([]);
  const [pets, setPets] = useState<Pet[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savedSearches, setSavedSearches] = useState<SavedSearch[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [filters, setFilters] = useState<FilterState>({
    species: 'All Species',
    breed: 'All Breeds',
    age: 'All Ages',
    size: 'All Sizes',
    temperament: 'All Temperaments',
    specialNeeds: 'All',
    location: 'All Locations',
    searchQuery: ''
  });

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  // Fetch pets when filters or page changes
  useEffect(() => {
    if (!isHydrated) return;

    const abortController = new AbortController();
    let isMounted = true;

    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        // Build filter object for API call
        const apiFilters: any = {
          status: 'available',
          page: currentPage,
          limit: ITEMS_PER_PAGE
        };

        // Map species filter to category enum
        if (filters.species !== 'All Species') {
          const categoryEnum = mapCategoryToEnum(filters.species);
          if (categoryEnum) {
            apiFilters.category = categoryEnum;
          }
        }

        // Add breed filter
        if (filters.breed !== 'All Breeds') {
          apiFilters.breed = filters.breed;
        }

        // Add location filter
        if (filters.location !== 'All Locations') {
          apiFilters.location = filters.location;
        }

        // Add size filter
        if (filters.size !== 'All Sizes') {
          apiFilters.size = filters.size;
        }

        // Parse age filter
        if (filters.age !== 'All Ages') {
          if (filters.age === 'Puppy/Kitten (0-1 year)') {
            apiFilters.ageMin = 0;
            apiFilters.ageMax = 1;
          } else if (filters.age === 'Young (1-3 years)') {
            apiFilters.ageMin = 1;
            apiFilters.ageMax = 3;
          } else if (filters.age === 'Adult (3-7 years)') {
            apiFilters.ageMin = 3;
            apiFilters.ageMax = 7;
          } else if (filters.age === 'Senior (7+ years)') {
            apiFilters.ageMin = 7;
          }
        }

        const { data, count, error: fetchError } = await fetchPets(apiFilters, abortController.signal);

        // Only update state if component is still mounted and request wasn't aborted
        if (!isMounted || abortController.signal.aborted) return;

        if (fetchError) {
          setError('Failed to load pets. Please try again.');
          setPets([]);
          setTotalCount(0);
          return;
        }

        setTotalCount(count || 0);

        // Transform database pets to UI pets
        const transformedPets: Pet[] = (data || [])
          .map((pet: DatabasePet) => ({
            id: pet.id,
            name: pet.name,
            species: getCategoryDisplay(pet.category),
            breed: pet.breed,
            age: formatAge(pet.age_years, pet.age_months),
            size: pet.size ? `${pet.size}` : 'Unknown',
            gender: pet.gender,
            temperament: pet.personality_traits || [],
            location: pet.shelter_location || 'Location not specified',
            distance: 'Distance not available',
            image: pet.image_url || 'https://images.unsplash.com/photo-1591200687582-77abaf45de25',
            alt: `${pet.name} - ${pet.breed} ${getCategoryDisplay(pet.category)} available for adoption`,
            shelterName: pet.shelter_location || 'Shelter',
            specialNeeds: false,
            vaccinated: pet.vaccination_status?.toLowerCase().includes('up to date') || false,
            spayedNeutered: pet.spayed_neutered || false,
            compatibilityScore: Math.floor(Math.random() * 20) + 80, // Mock compatibility
            quickFacts: [
              pet.good_with_kids ? 'Good with kids' : '',
              pet.good_with_pets ? 'Good with other pets' : '',
              pet.energy_level ? `Energy: ${pet.energy_level}` : ''
            ].filter(Boolean)
          }));

        setPets(transformedPets);

        // Scroll to top on page change
        window.scrollTo({ top: 0, behavior: 'smooth' });

      } catch (err) {
        if (!isMounted || abortController.signal.aborted) return;
        console.error('Error loading pets:', err);
        setError('An unexpected error occurred. Please try again.');
        setPets([]);
      } finally {
        if (isMounted && !abortController.signal.aborted) {
          setLoading(false);
        }
      }
    };

    fetchData();

    // Cleanup function
    return () => {
      isMounted = false;
      abortController.abort();
    };
  }, [filters, currentPage, isHydrated]);

  useEffect(() => {
    // Reset to page 1 when filters change
    setCurrentPage(1);
  }, [filters]);

  useEffect(() => {
    if (user) {
      loadSavedSearches();
    } else {
      setSavedSearches([]);
    }
  }, [user]);

  const loadSavedSearches = async () => {
    try {
      const searches = await savedSearchService.getSavedSearches();
      setSavedSearches(searches);
    } catch (err) {
      console.error('Failed to load saved searches:', err);
    }
  };

  const handleFilterChange = (newFilters: FilterState) => {
    setFilters(newFilters);
  };

  const handleSaveSearch = async () => {
    if (!user) return;

    // Create a name based on filters
    const nameParts = [];
    if (filters.species !== 'All Species') nameParts.push(filters.species);
    if (filters.breed !== 'All Breeds') nameParts.push(filters.breed);
    if (filters.location !== 'All Locations') nameParts.push(filters.location);
    if (nameParts.length === 0) nameParts.push('All Pets');

    const name = nameParts.join(' • ');

    try {
      await savedSearchService.createSavedSearch(name, filters, pets.length);
      await loadSavedSearches();
    } catch (err) {
      console.error('Failed to save search:', err);
      // Ideally show a toast notification here
    }
  };

  const handleCompareToggle = (petId: string) => {
    setCompareList((prev) => {
      if (prev.includes(petId)) {
        return prev.filter((id) => id !== petId);
      }
      if (prev.length >= 3) {
        return prev;
      }
      return [...prev, petId];
    });
  };

  const handleClearComparison = () => {
    setCompareList([]);
  };

  const handleRemoveFromComparison = (petId: string) => {
    setCompareList((prev) => prev.filter((id) => id !== petId));
  };

  const handleLoadSearch = (searchId: string) => {
    const search = savedSearches.find(s => s.id === searchId);
    if (search) {
      setFilters(search.filters);
    }
  };

  const handleDeleteSearch = async (searchId: string) => {
    try {
      await savedSearchService.deleteSavedSearch(searchId);
      await loadSavedSearches();
    } catch (err) {
      console.error('Failed to delete search:', err);
    }
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= Math.ceil(totalCount / ITEMS_PER_PAGE)) {
      setCurrentPage(newPage);
    }
  };

  const sortedPets = [...pets].sort((a, b) => {
    if (sortBy === 'compatibility') return b.compatibilityScore - a.compatibilityScore;
    if (sortBy === 'distance') return 0; // Distance sorting not yet implemented
    if (sortBy === 'age') return a.age.localeCompare(b.age);
    return 0;
  });

  const comparedPets = pets.filter((pet) => compareList.includes(pet.id));
  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-background pt-24 pb-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse space-y-8">
            <div className="h-12 bg-muted rounded-lg w-1/3" />
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              <div className="lg:col-span-1 h-96 bg-muted rounded-lg" />
              <div className="lg:col-span-3 space-y-4">
                <div className="h-16 bg-muted rounded-lg" />
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                  {[1, 2, 3, 4, 5, 6].map((i) => (
                    <div key={i} className="h-96 bg-muted rounded-lg" />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-2 pb-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-4xl md:text-5xl font-headline font-bold text-foreground mb-4">
            Discover Your Perfect Companion
          </h1>
          <p className="text-lg text-muted-foreground max-w-3xl">
            Browse through our comprehensive database of pets waiting for their forever homes. Use our advanced filters and AI-powered matching to find the perfect addition to your family.
          </p>
        </div>

        <SavedSearches
          searches={savedSearches}
          onLoad={handleLoadSearch}
          onDelete={handleDeleteSearch}
        />

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <div className="lg:col-span-1">
            <FilterPanel
              onFilterChange={handleFilterChange}
              activeFilters={filters}
              onSaveSearch={handleSaveSearch}
              canSave={!!user}
            />
          </div>

          <div className="lg:col-span-3">
            <div className="bg-card rounded-lg shadow-md border border-border p-4 mb-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between space-y-4 sm:space-y-0">
                <div className="flex items-center space-x-4">
                  <span className="text-sm font-semibold text-foreground">
                    {loading ? 'Loading...' : `${totalCount} pets found`}
                  </span>
                  <div className="flex items-center space-x-2">
                    <label htmlFor="sort" className="text-sm text-muted-foreground">
                      Sort by:
                    </label>
                    <select
                      id="sort"
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="px-3 py-1.5 bg-background border border-input rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    >
                      <option value="compatibility">Best Match</option>
                      <option value="distance">Nearest</option>
                      <option value="age">Age</option>
                    </select>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-2 rounded-lg transition-colors ${viewMode === 'grid' ? 'bg-primary text-white' : 'bg-muted text-foreground hover:bg-muted/80'
                      }`}
                    aria-label="Grid view"
                  >
                    <Icon
                      name="Squares2X2Icon"
                      size={20}
                      variant={viewMode === 'grid' ? 'solid' : 'outline'}
                    />
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-2 rounded-lg transition-colors ${viewMode === 'list' ? 'bg-primary text-white' : 'bg-muted text-foreground hover:bg-muted/80'
                      }`}
                    aria-label="List view"
                  >
                    <Icon
                      name="ListBulletIcon"
                      size={20}
                      variant={viewMode === 'list' ? 'solid' : 'outline'}
                    />
                  </button>
                </div>
              </div>
            </div>

            {error && (
              <div className="bg-destructive/10 border border-destructive text-destructive rounded-lg p-4 mb-6">
                <div className="flex items-start space-x-3">
                  <Icon name="ExclamationTriangleIcon" size={20} variant="solid" className="mt-0.5" />
                  <div>
                    <p className="font-semibold">Error loading pets</p>
                    <p className="text-sm">{error}</p>
                  </div>
                </div>
              </div>
            )}

            {loading ? (
              <div className="grid gap-6 grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <div key={i} className="animate-pulse">
                    <div className="bg-muted rounded-lg h-96" />
                  </div>
                ))}
              </div>
            ) : sortedPets.length === 0 ? (
              <div className="bg-card rounded-lg shadow-md border border-border p-12 text-center">
                <Icon
                  name="MagnifyingGlassIcon"
                  size={48}
                  variant="outline"
                  className="text-muted-foreground mx-auto mb-4"
                />
                <h3 className="text-xl font-headline font-semibold text-foreground mb-2">
                  No pets found
                </h3>
                <p className="text-muted-foreground mb-6">
                  Try adjusting your filters to see more results
                </p>
                <button
                  onClick={() =>
                    handleFilterChange({
                      species: 'All Species',
                      breed: 'All Breeds',
                      age: 'All Ages',
                      size: 'All Sizes',
                      temperament: 'All Temperaments',
                      specialNeeds: 'All',
                      location: 'All Locations',
                      searchQuery: ''
                    })
                  }
                  className="px-6 py-2.5 bg-primary text-white font-semibold rounded-lg hover:bg-primary/90 transition-colors"
                >
                  Clear All Filters
                </button>
              </div>
            ) : (
              <StaggerContainer
                className={`grid gap-6 ${viewMode === 'grid' ? 'grid-cols-1 md:grid-cols-2 xl:grid-cols-3' : 'grid-cols-1'
                  }`}
              >
                {sortedPets.map((pet) => (
                  <StaggerItem key={pet.id}>
                    <PetCard
                      pet={pet}
                      onCompare={handleCompareToggle}
                      isComparing={compareList.includes(pet.id)}
                    />
                  </StaggerItem>
                ))}
              </StaggerContainer>
            )}

            {/* Pagination Controls */}
            {totalCount > ITEMS_PER_PAGE && (
              <div className="mt-8 flex justify-center items-center space-x-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="p-2 rounded-lg border border-border hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Icon name="ChevronLeftIcon" size={20} variant="outline" />
                </button>

                <div className="flex space-x-2">
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(page => {
                      // Show first, last, current, and surrounding pages
                      return page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1;
                    })
                    .map((page, index, array) => (
                      <React.Fragment key={page}>
                        {index > 0 && array[index - 1] !== page - 1 && (
                          <span className="px-2 self-center">...</span>
                        )}
                        <button
                          onClick={() => handlePageChange(page)}
                          className={`w-10 h-10 rounded-lg font-medium transition-colors ${currentPage === page
                            ? 'bg-primary text-white'
                            : 'bg-card text-foreground hover:bg-muted border border-border'
                            }`}
                        >
                          {page}
                        </button>
                      </React.Fragment>
                    ))
                  }
                </div>

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="p-2 rounded-lg border border-border hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Icon name="ChevronRightIcon" size={20} variant="outline" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <ComparisonPanel
        pets={comparedPets}
        onRemove={handleRemoveFromComparison}
        onClear={handleClearComparison}
      />
    </div>
  );
};

export default PetDiscoveryInteractive;